import { useState, useEffect } from 'react';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { apiService } from '../../services/api';

interface NotificationBannerProps {
  applicationId: string;
}

const NotificationBanner = ({ applicationId }: NotificationBannerProps) => {
  const { client } = useClientAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    checkUnreadMessages();
    // Check for unread messages every 30 seconds
    const interval = setInterval(checkUnreadMessages, 30000);
    return () => clearInterval(interval);
  }, [applicationId]);

  const checkUnreadMessages = async () => {
    try {
      const response = await apiService.getApplicationMessages(applicationId);
      if (response.status === 'success') {
        const unreadMessages = response.data.messages.filter(
          (msg: any) => msg.senderType === 'admin' && !msg.isRead
        );
        setUnreadCount(unreadMessages.length);
        setShowBanner(unreadMessages.length > 0);
      }
    } catch (error) {
      console.error('Failed to check unread messages:', error);
    }
  };

  if (!showBanner) return null;

  return (
    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-blue-700">
            You have {unreadCount} new message{unreadCount !== 1 ? 's' : ''} from our team.
            <a
              href={`/client/applications/${applicationId}/messages`}
              className="font-medium underline text-blue-700 hover:text-blue-600 ml-1"
            >
              View messages
            </a>
          </p>
        </div>
        <div className="ml-auto pl-3">
          <div className="-mx-1.5 -my-1.5">
            <button
              onClick={() => setShowBanner(false)}
              className="inline-flex bg-blue-50 rounded-md p-1.5 text-blue-500 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-50 focus:ring-blue-600"
            >
              <span className="sr-only">Dismiss</span>
              <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationBanner;
